import { ConductReviewEntity } from 'syft-acp-core/store/types'
import { SUPPORTED_COUNTRY_CODES } from 'syft-acp-core/lib/i18n'
import * as structureTypes from 'syft-acp-core/components/EditableTable'
import { CountryCode } from './BanReviewDetail.types'
import { differenceInMinutes, formatDistanceToNow, formatDistanceToNowStrict } from 'date-fns'

const DISPLAY_IN_HOURS_THRESHOLD = 96 * 60 // 96 hours in minutes
export const formatBanReviewElapsedTime = (created_at: string) => {
  const createdDate = new Date(created_at)
  const minutesAgo = differenceInMinutes(new Date(), createdDate)

  if (minutesAgo < DISPLAY_IN_HOURS_THRESHOLD) {
    return formatDistanceToNowStrict(createdDate, {
      addSuffix: true,
      unit: minutesAgo < 60 ? 'minute' : 'hour',
      roundingMethod: 'floor',
    })
  } else {
    return formatDistanceToNow(createdDate, {
      addSuffix: true,
    })
  }
}

export const getDataStructure = (isClosed = false, country: CountryCode) => [
  {
    title: 'Ban review information',
    structure: [
      [
        'created_at',
        {
          header: 'Created',
          type: structureTypes.TYPE_DATE,
          parameters: {
            formatRelative: formatBanReviewElapsedTime,
          },
        },
      ],
      [
        'assigned_at',
        {
          header: 'Assigned',
          type: structureTypes.TYPE_DATE,
        },
      ],
      [
        'assignee.id',
        {
          header: 'Assigned to',
          editable: true,
          type: structureTypes.TYPE_BAN_REVIEW_ASSIGNED_DROPDOWN,
        },
      ],
      [
        'notes',
        {
          header: 'Notes',
          editable: !isClosed,
          type: structureTypes.TYPE_TEXTAREA,
        },
      ],
      [
        'outcome',
        {
          header: 'Outcome',
          editable: !isClosed,
          type: structureTypes.TYPE_CONDUCT_REVIEW_OUTCOME_DROPDOWN,
        },
      ],
      isClosed && [
        'ban.banner.full_name',
        {
          header: 'Banned by',
        },
      ],
      isClosed && [
        'ban.banned_at',
        {
          header: 'Ban date',
          type: structureTypes.TYPE_DATE,
        },
      ],
    ].filter(Boolean),
  },
  {
    title: 'Worker information',
    structure: [
      [
        'worker.salesforce_url',
        {
          header: 'Salesforce',
          type: structureTypes.TYPE_LINK,
          parameters: {
            to: ({ worker }: Partial<ConductReviewEntity>) => worker && worker.salesforce_url,
            target: '_blank',
            external: true,
          },
        },
      ],
      [
        'worker.id',
        {
          header: 'Worker ID',
          type: structureTypes.TYPE_LINK,
          parameters: {
            to: ({ worker }: Partial<ConductReviewEntity>) => worker && `/entity/workers/view/${worker.id}`,
          },
        },
      ],
      [
        'worker.email',
        {
          header: 'Appeals',
          type: structureTypes.TYPE_LINK,
          parameters: {
            to: ({ worker }: Partial<ConductReviewEntity>) =>
              worker && `/conduct/appeals/list?email=${worker.email}`,
          },
        },
      ],
      [
        'worker.full_name',
        {
          header: 'Name',
        },
      ],
      [
        'federated_state_code',
        {
          header: 'State',
          showForCountries: [SUPPORTED_COUNTRY_CODES.US],
        },
      ],
      [
        'worker.id',
        {
          header: 'Flex Levels',
          type: structureTypes.TYPE_LINK,
          parameters: {
            external: true,
            to: ({ worker }: Partial<ConductReviewEntity>) =>
              worker &&
              `https://flex-redash.indeed.tech/queries/24494?p_Worker%20ID=$${worker?.id}${country}`,
          },
          calc: () => {
            return 'Details'
          },
        },
      ],
    ].filter(Boolean),
  },
]
